/**
 * AC Process API Service
 * Complete API service for all 170 AC Process modes
 * Maps C# ACProcess.aspx functionality to React/Node.js API calls
 */

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/reactApi/api';
const AC_PROCESS_URL = `${API_BASE_URL}/ac-process`;

// ==================== AC DATE MANAGEMENT ====================

/**
 * Mode 1: Insert AC Date
 * C#: btnSubmit_Click -> mode=3
 */
export const insertACDate = async (data) => {
    try {
        const response = await axios.post(`${AC_PROCESS_URL}/ac-date`, {
            date: data.date,
            dateTo: data.dateTo,
            acType: data.acType
        });
        return response.data;
    } catch (error) {
        console.error('insertACDate error:', error);
        throw error;
    }
};

/**
 * Mode 2: Get AC Dates by Type
 * C#: binddate() -> mode=2
 */
export const getACDatesByType = async (acType) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/ac-dates/${acType}`);
        return response.data;
    } catch (error) {
        console.error('getACDatesByType error:', error);
        throw error;
    }
};

/**
 * Mode 6: Get AC Dates Dropdown
 * C#: Used in date selection dropdowns
 */
export const getACDatesDropdown = async (acType, year, month) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/ac-dates/dropdown/list`, {
            params: { acType, year, month }
        });
        return response.data;
    } catch (error) {
        console.error('getACDatesDropdown error:', error);
        throw error;
    }
};

/**
 * Mode 18: Get All AC Dates
 */
export const getAllACDates = async () => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/ac-dates`);
        return response.data;
    } catch (error) {
        console.error('getAllACDates error:', error);
        throw error;
    }
};

/**
 * Mode 19: Get AC Date by ID
 */
export const getACDateById = async (id) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/ac-date/${id}`);
        return response.data;
    } catch (error) {
        console.error('getACDateById error:', error);
        throw error;
    }
};

/**
 * Mode 20: Update AC Date
 * C#: rptrdate_ItemCommand -> Edit mode=4
 */
export const updateACDate = async (data) => {
    try {
        const response = await axios.put(`${AC_PROCESS_URL}/ac-date`, {
            date: data.date,
            dateTo: data.dateTo,
            acType: data.acType,
            id: data.id
        });
        return response.data;
    } catch (error) {
        console.error('updateACDate error:', error);
        throw error;
    }
};

/**
 * Mode 21: Check Existing AC Date
 * C#: checkexist() -> mode=21
 */
export const checkExistingACDates = async (data) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/ac-date/validate/check`, {
            params: { 
                acType: data.acType, 
                date: data.date,
                dateTo: data.dateTo,
            }
        });
        return response.data;
    } catch (error) {
        console.error('checkExistingACDates error:', error);
        throw error;
    }
};

/**
 * Mode 22: Deactivate AC Date
 * C#: rptrdate_ItemCommand -> Delete mode=14
 */
export const deactivateACDate = async (id) => {
    try {
        const response = await axios.put(`${AC_PROCESS_URL}/ac-date/deactivate`, { id });
        return response.data;
    } catch (error) {
        console.error('deactivateACDate error:', error);
        throw error;
    }
};

/**
 * Mode 41: Get AC DateTo by Type
 * C#: ddlDate_SelectedIndexChanged -> mode=41
 */
export const getACDateToByType = async (acType, date) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/ac-date-to/${acType}`, {
            params: { date }
        });
        return response.data;
    } catch (error) {
        console.error('getACDateToByType error:', error);
        throw error;
    }
};

/**
 * Mode 73: Get Recent AC Dates
 */
export const getRecentACDates = async (acType) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/ac-dates/recent/list`, {
            params: { acType }
        });
        return response.data;
    } catch (error) {
        console.error('getRecentACDates error:', error);
        throw error;
    }
};

/**
 * Mode 143: Get AC Date Years
 */
export const getACDateYears = async (acType) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/ac-date/years/list`, {
            params: { acType }
        });
        return response.data;
    } catch (error) {
        console.error('getACDateYears error:', error);
        throw error;
    }
};

/**
 * Mode 144: Get AC Dates by Year and Month
 */
export const getACDatesByYearMonth = async (acType, year, month) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/month-ac-dates/by-year-month`, {
            params: { acType, year, month }
        });
        return response.data;
    } catch (error) {
        console.error('getACDatesByYearMonth error:', error);
        throw error;
    }
};

/**
 * Mode 74: Get AC Date To Options
 */
export const getACDateToOptions = async (acType, date) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/ac-date-to/options/list`, {
            params: { acType, date }
        });
        return response.data;
    } catch (error) {
        console.error('getACDateToOptions error:', error);
        throw error;
    }
};

// ==================== AC PROCESS MANAGEMENT ====================

/**
 * Mode 3: Insert AC Process
 * C#: btnSubmit_Click -> mode=3
 */
export const insertACProcess = async (data) => {
    try {
        const response = await axios.post(`${AC_PROCESS_URL}/insert`, {
            appid: data.appid,
            acDate: data.acDate,
            insertedBy: data.insertedBy,
            acType: data.acType,
            acDateTo: data.acDateTo,
            doRemarks: data.doRemarks,
            generateNewCertNo: data.generateNewCertNo
        });
        return response.data;
    } catch (error) {
        console.error('insertACProcess error:', error);
        throw error;
    }
};

/**
 * Mode 4: Update AC Process
 * C#: btnSubmit_Click -> mode=4 (when updating)
 */
export const updateACProcess = async (data) => {
    try {
        const response = await axios.put(`${AC_PROCESS_URL}/update`, {
            appid: data.appid,
            id: data.id,
            acDate: data.acDate,
            updatedBy: data.updatedBy,
            acType: data.acType,
            acDateTo: data.acDateTo,
            doRemarks: data.doRemarks
        });
        return response.data;
    } catch (error) {
        console.error('updateACProcess error:', error);
        throw error;
    }
};

/**
 * Mode 76: Update AC Process Extended
 */
export const updateACProcessExtended = async (data) => {
    try {
        const response = await axios.put(`${AC_PROCESS_URL}/extended`, {
            appid: data.appid,
            id: data.id,
            acDate: data.acDate,
            updatedBy: data.updatedBy,
            acType: data.acType,
            acDateTo: data.acDateTo,
            doRemarks: data.doRemarks,
            ...data // Include any additional fields
        });
        return response.data;
    } catch (error) {
        console.error('updateACProcessExtended error:', error);
        throw error;
    }
};

/**
 * Mode 5: Get AC Process by AppId
 */
export const getACProcessByAppId = async (appid) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/${appid}`);
        return response.data;
    } catch (error) {
        console.error('getACProcessByAppId error:', error);
        throw error;
    }
};

/**
 * Mode 171: Get Application Details (Stage, LabTypeId, AssessmentTypeId, AssessmentType)
 * C#: MODE 171 -> SELECT Stage, LabTypeId, AssessmentTypeId, AssessmentType FROM LabApplications
 */
export const getApplicationDetails = async (appid) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/application/details/${appid}`);
        return response.data;
    } catch (error) {
        console.error('getApplicationDetails error:', error);
        throw error;
    }
};

/**
 * Mode 13: Get AC Process Details with History
 * C#: binddaterepeater() -> mode=13
 */
export const getACProcessDetails = async (appid, orgid) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/details/${appid}/${orgid}`);
        return response.data;
    } catch (error) {
        console.error('getACProcessDetails error:', error);
        throw error;
    }
};

/**
 * Mode 14: Deactivate AC Process
 * C#: rptrdate_ItemCommand -> Delete
 */
export const deactivateACProcess = async (appid, id) => {
    try {
        const response = await axios.put(`${AC_PROCESS_URL}/deactivate`, {
            appid,
            id
        });
        return response.data;
    } catch (error) {
        console.error('deactivateACProcess error:', error);
        throw error;
    }
};

/**
 * Mode 16: Get AC Status and Remarks
 * C#: bindStatus() -> mode=16
 */
export const getACStatusRemarks = async (appid) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/status-remarks/${appid}`);
        return response.data;
    } catch (error) {
        console.error('getACStatusRemarks error:', error);
        throw error;
    }
};

/**
 * Mode 67: Rollback AC Process
 */
export const rollbackACProcess = async (appid, updatedBy) => {
    try {
        const response = await axios.put(`${AC_PROCESS_URL}/rollback`, {
            appid,
            updatedBy
        });
        return response.data;
    } catch (error) {
        console.error('rollbackACProcess error:', error);
        throw error;
    }
};

/**
 * Mode 129: Reset AC Process (Rollback Decision)
 * Used by Convener to reset AC process
 */
export const resetACProcess = async (appid, id, orgid) => {
    try {
        const response = await axios.put(`${AC_PROCESS_URL}/reset`, {
            appid,
            id,
            orgid
        });
        return response.data;
    } catch (error) {
        console.error('resetACProcess error:', error);
        throw error;
    }
};

// ==================== MOM FILE MANAGEMENT ====================

/**
 * Mode 8: Upload MOM File
 */
export const uploadMOMFile = async (data) => {
    try {
        const response = await axios.post(`${AC_PROCESS_URL}/mom-file`, {
            momFile: data.momFile,
            updatedBy: data.updatedBy,
            acDate: data.acDate,
            acDateTo: data.acDateTo,
            acType: data.acType,
            approvalDate: data.approvalDate,
            momFormat: data.momFormat
        });
        return response.data;
    } catch (error) {
        console.error('uploadMOMFile error:', error);
        throw error;
    }
};

/**
 * Mode 72: Auto-fill MOM File
 */
export const autoFillMOMFile = async (data) => {
    try {
        const response = await axios.put(`${AC_PROCESS_URL}/mom-file/auto-fill`, {
            appid: data.appid,
            id: data.id,
            acDate: data.acDate,
            acDateTo: data.acDateTo
        });
        return response.data;
    } catch (error) {
        console.error('autoFillMOMFile error:', error);
        throw error;
    }
};

/**
 * Mode 137: Get MOM Files All
 */
export const getMOMFilesAll = async (acType, acDate, acDateTo) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/mom-files/all`, {
            params: { acType, acDate, acDateTo }
        });
        return response.data;
    } catch (error) {
        console.error('getMOMFilesAll error:', error);
        throw error;
    }
};

/**
 * Mode 9: Get Uploaded MOM Files
 */
export const getUploadedMOMFiles = async () => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/mom/get-mom-files`);
        return response.data;
    } catch (error) {
        console.error('getUploadedMOMFiles error:', error);
        throw error;
    }
};

/**
 * Mode 11: Get MOM File by AppId
 */
export const getMOMFileByAppId = async (appid) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/mom-file/${appid}`);
        return response.data;
    } catch (error) {
        console.error('getMOMFileByAppId error:', error);
        throw error;
    }
};

/**
 * Mode 82: Get MOM Format
 */
export const getMOMFormat = async (appid) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/mom-format/${appid}`);
        return response.data;
    } catch (error) {
        console.error('getMOMFormat error:', error);
        throw error;
    }
};

// ==================== AC STATUS MANAGEMENT ====================

/**
 * Mode 12: Update AC Status
 * C#: btnfinalStatus_Click or lnkFinal_Click1 -> mode=12
 */
export const updateACStatus = async (data) => {
    try {
        const response = await axios.put(`${AC_PROCESS_URL}/ac-status`, {
            appid: data.appid,
            id: data.id,
            acStatus: data.acStatus,
            remarks: data.remarks,
            updatedBy: data.updatedBy,
            isActive: data.isActive,
            fromDate: data.fromDate,
            toDate: data.toDate,
            amendmentDate: data.amendmentDate,
            certNo: data.certNo,
            issueDate: data.issueDate,
            discipline: data.discipline,
            isAddressWithIn30: data.isAddressWithIn30,
            markForAssessment: data.markForAssessment,
            assessmentType: data.assessmentType,
            iacMinutes: data.iacMinutes,
            showYear: data.showYear
        });
        return response.data;
    } catch (error) {
        console.error('updateACStatus error:', error);
        throw error;
    }
};

/**
 * Mode 134: Update AC Status Extended (with NextDueActivity and ExpectedDate)
 * C#: lnkFinal_Click -> mode=134 (Frankie's new version)
 */
export const updateACStatusExtended = async (data) => {
    try {
        const response = await axios.put(`${AC_PROCESS_URL}/ac-status/extended`, {
            appid: data.appid,
            id: data.id,
            acStatus: data.acStatus,
            remarks: data.remarks,
            updatedBy: data.updatedBy,
            isActive: data.isActive,
            fromDate: data.fromDate,
            toDate: data.toDate,
            amendmentDate: data.amendmentDate,
            certNo: data.certNo,
            issueDate: data.issueDate,
            discipline: data.discipline,
            isAddressWithIn30: data.isAddressWithIn30,
            markForAssessment: data.markForAssessment,
            assessmentType: data.assessmentType,
            iacMinutes: data.iacMinutes,
            nextDueActivity: data.nextDueActivity,
            expectedDate: data.expectedDate
        });
        return response.data;
    } catch (error) {
        console.error('updateACStatusExtended error:', error);
        throw error;
    }
};

/**
 * Mode 54: Mark Status
 * C#: btnSubmitStatus_Click -> mode=54
 */
export const markStatus = async (appid) => {
    try {
        const response = await axios.put(`${AC_PROCESS_URL}/ac-status/mark`, { appid });
        return response.data;
    } catch (error) {
        console.error('markStatus error:', error);
        throw error;
    }
};

/**
 * Mode 115: Update Committee Decision
 */
export const updateCommitteeDecision = async (appid, acStatus, remarks) => {
    try {
        const response = await axios.put(`${AC_PROCESS_URL}/committee-decision`, {
            appid,
            acStatus,
            remarks
        });
        return response.data;
    } catch (error) {
        console.error('updateCommitteeDecision error:', error);
        throw error;
    }
};

// ==================== APPLICATIONS MANAGEMENT ====================

/**
 * Mode 7: Get Applications
 */
export const getApplications = async (condition) => {
    try {
        const response = await axios.post(`${AC_PROCESS_URL}/applications`, { condition });
        return response.data;
    } catch (error) {
        console.error('getApplications error:', error);
        throw error;
    }
};

/**
 * Mode 65: Search Applications
 */
export const searchApplications = async (username) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/applications/search`, {
            params: { username }
        });
        return response.data;
    } catch (error) {
        console.error('searchApplications error:', error);
        throw error;
    }
};

/**
 * sp_Common Mode 58: Get Applications List by Username (for dropdown)
 */
export const getApplicationsByUsername = async (username) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/applications/list-by-username`, {
            params: { username }
        });
        return response.data;
    } catch (error) {
        console.error('getApplicationsByUsername error:', error);
        throw error;
    }
};

/**
 * Mode 17: Get Applications with Condition
 */
export const getApplicationsWithCondition = async (acType, acDate, acDateTo, condition) => {
    try {
        const response = await axios.post(`${AC_PROCESS_URL}/applications/with-condition`, {
            acType,
            acDate,
            acDateTo,
            condition
        });
        return response.data;
    } catch (error) {
        console.error('getApplicationsWithCondition error:', error);
        throw error;
    }
};

/**
 * Mode 142: Get Application by AppId and Username
 */
export const getApplicationByAppIdUsername = async (appid, username) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/application/${appid}/user`, {
            params: { username }
        });
        return response.data;
    } catch (error) {
        console.error('getApplicationByAppIdUsername error:', error);
        throw error;
    }
};

/**
 * Mode 127: Get Applications by OrgId and Stage
 */
export const getApplicationsByOrgIdAndStage = async (orgid) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/applications/org-stage`, {
            params: { orgid }
        });
        return response.data;
    } catch (error) {
        console.error('getApplicationsByOrgIdAndStage error:', error);
        throw error;
    }
};

/**
 * Mode 173: Get Previous Applications by OrgId
 */
export const getApplicationsByOrgIdPrevious = async (orgid) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/applications/org-previous`, {
            params: { orgid }
        });
        return response.data;
    } catch (error) {
        console.error('getApplicationsByOrgIdPrevious error:', error);
        throw error;
    }
};

/**
 * Mode 174: Pull Scopes From Previous Application
 */
export const pullScopesFromPreviousApp = async (appid, oldappid, orgId) => {
    try {
        const response = await axios.post(`${AC_PROCESS_URL}/scope/pull-from-previous-app`, {
            appid,
            oldappid,
            orgId
        });
        return response.data;
    } catch (error) {
        console.error('pullScopesFromPreviousApp error:', error);
        throw error;
    }
};

// ==================== CERTIFICATE MANAGEMENT ====================

/**
 * Mode 24: Get Certificate Details
 */
export const getCertificateDetails = async (appid) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/certificate/${appid}`);
        return response.data;
    } catch (error) {
        console.error('getCertificateDetails error:', error);
        throw error;
    }
};

/**
 * Mode 48: Get Certificate Details for Printing
 */
export const getCertificateForPrint = async (appid) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/certificate/print/${appid}`);
        return response.data;
    } catch (error) {
        console.error('getCertificateForPrint error:', error);
        throw error;
    }
};

/**
 * Mode 118: Generate Certificate Number
 * C#: btngeneratepassword_Click -> mode=118
 */
export const generateCertificateNumber = async (appid, labTypeId, ceoApprove = 0) => {
    try {
        const response = await axios.post(`${AC_PROCESS_URL}/certificate/generate-number`, {
            appid,
            labTypeId,
            ceoApprove
        });
        return response.data;
    } catch (error) {
        console.error('generateCertificateNumber error:', error);
        throw error;
    }
};

/**
 * Mode 119: Check Certificate Number
 */
export const checkCertificateNumber = async (appid) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/certificate/check-number/${appid}`);
        return response.data;
    } catch (error) {
        console.error('checkCertificateNumber error:', error);
        throw error;
    }
};

/**
 * Mode 120: Get Certificates by OrgId
 */
export const getCertificatesByOrgId = async (orgid) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/certificates/org`, {
            params: { orgid }
        });
        return response.data;
    } catch (error) {
        console.error('getCertificatesByOrgId error:', error);
        throw error;
    }
};

/**
 * Mode 153: CEO Approve Certificate
 */
export const ceoApproveCertificate = async (appid, jdDenyReason, updatedBy) => {
    try {
        const response = await axios.put(`${AC_PROCESS_URL}/certificate/ceo-approve`, {
            appid,
            jdDenyReason,
            updatedBy
        });
        return response.data;
    } catch (error) {
        console.error('ceoApproveCertificate error:', error);
        throw error;
    }
};

/**
 * Mode 168: Update Certificate Dates and Details
 */
export const updateCertificateDatesDetails = async (data) => {
    try {
        const response = await axios.put(`${AC_PROCESS_URL}/certificate/dates-details`, data);
        return response.data;
    } catch (error) {
        console.error('updateCertificateDatesDetails error:', error);
        throw error;
    }
};

/**
 * Mode 170: Get Certificate Scope Count
 */
export const getCertificateScopeCount = async (appid) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/certificate/scope-count/${appid}`);
        return response.data;
    } catch (error) {
        console.error('getCertificateScopeCount error:', error);
        throw error;
    }
};

// ==================== GRANT LETTER MANAGEMENT ====================

/**
 * Mode 45: Get Grant Letter Details
 */
export const getGrantLetterDetails = async (appid, id) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/grant-letter/${appid}`, {
            params: { id }
        });
        return response.data;
    } catch (error) {
        console.error('getGrantLetterDetails error:', error);
        throw error;
    }
};

/**
 * Mode 50: Mark Grant Letter Sent
 * C#: btnSendGrantLetter_Click -> mode=50
 */
export const markGrantLetterSent = async (appid, id) => {
    try {
        const response = await axios.put(`${AC_PROCESS_URL}/grant-letter/mark-sent`, {
            appid,
            id
        });
        return response.data;
    } catch (error) {
        console.error('markGrantLetterSent error:', error);
        throw error;
    }
};

/**
 * Mode 51: Check Grant Letter Sent
 * C#: checkGrantLetterSent() -> mode=51
 */
export const checkGrantLetterSent = async (appid) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/grant-letter/check-sent/${appid}`);
        return response.data;
    } catch (error) {
        console.error('checkGrantLetterSent error:', error);
        throw error;
    }
};

/**
 * Mode 58: Send Grant Letter Final
 */
export const sendGrantLetterFinal = async (appid, updatedBy, orgid) => {
    try {
        const response = await axios.put(`${AC_PROCESS_URL}/grant-letter/send-final`, {
            appid,
            updatedBy,
            orgid
        });
        return response.data;
    } catch (error) {
        console.error('sendGrantLetterFinal error:', error);
        throw error;
    }
};

/**
 * Mode 84: Get Grant Letter History
 */
export const getGrantLetterHistory = async (appid) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/grant-letter/history/${appid}`);
        return response.data;
    } catch (error) {
        console.error('getGrantLetterHistory error:', error);
        throw error;
    }
};

/**
 * Mode 111: Get Grant Letter Latest
 */
export const getGrantLetterLatest = async (appid) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/grant-letter/latest/${appid}`);
        return response.data;
    } catch (error) {
        console.error('getGrantLetterLatest error:', error);
        throw error;
    }
};

// ==================== COMMUNICATION LETTER ====================

/**
 * Mode 450: Get Communication Letter Details
 */
export const getCommunicationLetterDetails = async (appid, id) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/communication-letter/${appid}`, {
            params: { id }
        });
        return response.data;
    } catch (error) {
        console.error('getCommunicationLetterDetails error:', error);
        throw error;
    }
};

/**
 * Mode 511: Check Communication Letter Sent
 */
export const checkCommunicationLetterSent = async (appid) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/communication-letter/check-sent/${appid}`);
        return response.data;
    } catch (error) {
        console.error('checkCommunicationLetterSent error:', error);
        throw error;
    }
};

/**
 * Mode 840: Get Communication History
 * C#: bindCommunicationletter() -> mode=840
 */
export const getCommunicationHistory = async (appid) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/communication-letter/history/${appid}`);
        return response.data;
    } catch (error) {
        console.error('getCommunicationHistory error:', error);
        throw error;
    }
};

/**
 * Mode 156: Check Communication Letter Status
 * C#: checkapp() -> mode=156
 */
export const checkCommunicationLetterStatus = async (appid) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/communication-letter/check-status/${appid}`);
        return response.data;
    } catch (error) {
        console.error('checkCommunicationLetterStatus error:', error);
        throw error;
    }
};

// ==================== DECISION MANAGEMENT ====================

/**
 * Mode 52: Get Remarks
 */
export const getRemarks = async (appid) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/decision/remarks/${appid}`);
        return response.data;
    } catch (error) {
        console.error('getRemarks error:', error);
        throw error;
    }
};

/**
 * Mode 53: Insert Advisory and Update Stage
 * C#: Button2_Click -> mode=53
 */
export const insertAdvisoryUpdateStage = async (updatedBy, appid, labTypeId, id) => {
    try {
        const response = await axios.put(`${AC_PROCESS_URL}/decision/advisory-stage`, {
            updatedBy,
            appid,
            labTypeId,
            id
        });
        return response.data;
    } catch (error) {
        console.error('insertAdvisoryUpdateStage error:', error);
        throw error;
    }
};

/**
 * Mode 56: Mark Decision
 */
export const markDecision = async (data) => {
    try {
        const response = await axios.post(`${AC_PROCESS_URL}/decision/mark`, data);
        return response.data;
    } catch (error) {
        console.error('markDecision error:', error);
        throw error;
    }
};

/**
 * Mode 57: Get Decision Details
 */
export const getDecisionDetails = async (appid) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/decision/${appid}`);
        return response.data;
    } catch (error) {
        console.error('getDecisionDetails error:', error);
        throw error;
    }
};

/**
 * Mode 62: Get Status Marked Details
 * C#: checkStatusMarked() -> mode=62
 */
export const getStatusMarkedDetails = async (appid) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/decision/status-marked/${appid}`);
        return response.data;
    } catch (error) {
        console.error('getStatusMarkedDetails error:', error);
        throw error;
    }
};

/**
 * Mode 63: Mark Decision Completed
 */
export const markDecisionCompleted = async (appid) => {
    try {
        const response = await axios.put(`${AC_PROCESS_URL}/decision/mark-completed`, { appid });
        return response.data;
    } catch (error) {
        console.error('markDecisionCompleted error:', error);
        throw error;
    }
};

// ==================== DO ADVISORY ====================

/**
 * Mode 27: Insert DO Advisory
 */
export const insertDOAdvisory = async (data) => {
    try {
        const response = await axios.post(`${AC_PROCESS_URL}/do-advisory`, data);
        return response.data;
    } catch (error) {
        console.error('insertDOAdvisory error:', error);
        throw error;
    }
};

/**
 * Mode 28: Get DO Advisory
 */
export const getDOAdvisory = async (appid) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/do-advisory/${appid}`);
        return response.data;
    } catch (error) {
        console.error('getDOAdvisory error:', error);
        throw error;
    }
};

/**
 * Mode 31: Get DO Advisory Details
 */
export const getDOAdvisoryDetails = async (appid, usertypeid) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/do-advisory/details/${appid}`, {
            params: { usertypeid }
        });
        return response.data;
    } catch (error) {
        console.error('getDOAdvisoryDetails error:', error);
        throw error;
    }
};

/**
 * Mode 39: Check Advisory Visibility
 */
export const checkAdvisoryVisibility = async (appid) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/do-advisory/check-visibility/${appid}`);
        return response.data;
    } catch (error) {
        console.error('checkAdvisoryVisibility error:', error);
        throw error;
    }
};

// ==================== FORWARD TO JD ====================

/**
 * Mode 35: Forward to JD
 * C#: btnSendtoDirector_Click -> mode=35
 */
export const forwardToJD = async (forwardToJD, updatedBy, appid) => {
    try {
        const response = await axios.put(`${AC_PROCESS_URL}/forward-to-jd`, {
            forwardToJD,
            updatedBy,
            appid
        });
        return response.data;
    } catch (error) {
        console.error('forwardToJD error:', error);
        throw error;
    }
};

/**
 * Mode 60: Forward to JD with AC Comment
 */
export const forwardToJDWithComment = async (appid, forwardToJD, acComment, updatedBy) => {
    try {
        const response = await axios.put(`${AC_PROCESS_URL}/forward-to-jd/with-comment`, {
            appid,
            forwardToJD,
            acComment,
            updatedBy
        });
        return response.data;
    } catch (error) {
        console.error('forwardToJDWithComment error:', error);
        throw error;
    }
};

/**
 * Mode 66: Get JD List
 * C#: bindDirectores() -> mode=66
 */
export const getJDList = async (orgid, labType) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/jd-list`, {
            params: { orgid, labType }
        });
        return response.data;
    } catch (error) {
        console.error('getJDList error:', error);
        throw error;
    }
};

/**
 * Mode 116: Get All JD List
 */
export const getAllJDList = async (orgid) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/jd-list/all`, {
            params: { orgid }
        });
        return response.data;
    } catch (error) {
        console.error('getAllJDList error:', error);
        throw error;
    }
};

/**
 * Mode 138: Check JD Acceptance
 */
export const checkJDAcceptance = async (appid) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/jd/check-acceptance/${appid}`);
        return response.data;
    } catch (error) {
        console.error('checkJDAcceptance error:', error);
        throw error;
    }
};

// ==================== DISCIPLINE & GROUP ====================

/**
 * Mode 40: Get Disciplines by LabType
 * C#: bindDisciplines() -> mode=40
 */
export const getDisciplinesByLabType = async (appid, labTypeId) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/disciplines/${appid}`, {
            params: { labTypeId }
        });
        return response.data;
    } catch (error) {
        console.error('getDisciplinesByLabType error:', error);
        throw error;
    }
};

/**
 * Mode 42: Get AC Date To by Type and Date
 */
export const getACDateToByDate = async (acType, date) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/ac-date-todate/type-and-date`, {
            params: { acType, date }
        });
        return response.data;
    } catch (error) {
        console.error('getACDateToByDate error:', error);
        throw error;
    }
};

/**
 * Mode 55: Get Groups by Discipline
 */
export const getGroupsByDiscipline = async (appid, labTypeId, disciplineid) => {
    try {
        const response = await axios.post(`${AC_PROCESS_URL}/groups/${appid}`, 
            { disciplineid },
            { params: { labTypeId } }
        );
        return response.data;
    } catch (error) {
        console.error('getGroupsByDiscipline error:', error);
        throw error;
    }
};

/**
 * Mode 165: Get Distinct Lab Types
 */
export const getDistinctLabTypes = async () => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/lab-types/distinct`);
        return response.data;
    } catch (error) {
        console.error('getDistinctLabTypes error:', error);
        throw error;
    }
};

// ==================== SCOPE MANAGEMENT ====================

/**
 * Mode 25: Update Scope Generated Flag
 * C#: hlGenerateScope_Click -> mode=25
 */
export const updateScopeGenerated = async (appid, insertedBy) => {
    try {
        const response = await axios.put(`${AC_PROCESS_URL}/scope/generated`, {
            appid,
            insertedBy
        });
        return response.data;
    } catch (error) {
        console.error('updateScopeGenerated error:', error);
        throw error;
    }
};

/**
 * Mode 26: Upload Scope
 * C#: btnUpload_Click -> mode=26
 */
export const uploadScope = async (appid, momFile, remarks, updatedBy) => {
    try {
        const response = await axios.post(`${AC_PROCESS_URL}/scope/upload`, {
            appid,
            momFile,
            remarks,
            updatedBy
        });
        return response.data;
    } catch (error) {
        console.error('uploadScope error:', error);
        throw error;
    }
};

/**
 * Mode 49: Copy Scope to Certificate
 * C#: UpdateCertficateScope() -> mode=49
 */
export const copyScopeToCertificate = async (appid) => {
    try {
        const response = await axios.post(`${AC_PROCESS_URL}/scope/copy-to-certificate`, {
            appid
        });
        return response.data;
    } catch (error) {
        console.error('copyScopeToCertificate error:', error);
        throw error;
    }
};

/**
 * Mode 97: Copy Previous Scope
 * C#: scopeextension(Type=1) -> mode=97
 */
export const copyPreviousScope = async (appid, orgid) => {
    try {
        const response = await axios.post(`${AC_PROCESS_URL}/scope/copy-previous`, {
            appid,
            orgid
        });
        return response.data;
    } catch (error) {
        console.error('copyPreviousScope error:', error);
        throw error;
    }
};

/**
 * Mode 100: Get Certificate Scope with Flag
 * C#: checkscopeextensiondata() -> mode=100
 */
export const getCertificateScopeWithFlag = async (appid) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/scope/certificate/${appid}`);
        return response.data;
    } catch (error) {
        console.error('getCertificateScopeWithFlag error:', error);
        throw error;
    }
};

/**
 * Mode 101: Copy and Insert Scope
 * C#: scopeextension(Type=2) -> mode=101
 */
export const copyAndInsertScope = async (appid, orgid) => {
    try {
        const response = await axios.post(`${AC_PROCESS_URL}/scope/copy-insert`, {
            appid,
            orgid
        });
        return response.data;
    } catch (error) {
        console.error('copyAndInsertScope error:', error);
        throw error;
    }
};

/**
 * Mode 125: Check Scope Generated
 * C#: checkScopeGen() -> mode=125
 */
export const checkScopeGenerated = async (appid) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/scope/check-generated/${appid}`);
        return response.data;
    } catch (error) {
        console.error('checkScopeGenerated error:', error);
        throw error;
    }
};

/**
 * Mode 146: Update Scope URL
 * C#: btngeneratescopefile_Click -> mode=146
 */
export const updateScopeURL = async (scopeURL, appid, orgid) => {
    try {
        const response = await axios.put(`${AC_PROCESS_URL}/scope/url`, {
            scopeURL,
            appid,
            orgid
        });
        return response.data;
    } catch (error) {
        console.error('updateScopeURL error:', error);
        throw error;
    }
};

/**
 * Mode 147: Get Scope URL
 */
export const getScopeURL = async (appid, phase) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/scope/url/${appid}`, {
            params: { phase }
        });
        return response.data;
    } catch (error) {
        console.error('getScopeURL error:', error);
        throw error;
    }
};

/**
 * Mode 148: Get All Scope URLs
 */
export const getAllScopeURLs = async (appid) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/scope/urls/${appid}`);
        return response.data;
    } catch (error) {
        console.error('getAllScopeURLs error:', error);
        throw error;
    }
};

// ==================== MISCELLANEOUS ====================

/**
 * Mode 47: Get Top AC Status
 * C#: checkFinalStatus() -> mode=47
 */
export const getTopACStatus = async (appid) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/grant-letter/ac-status/${appid}`);
        return response.data;
    } catch (error) {
        console.error('getTopACStatus error:', error);
        throw error;
    }
};

/**
 * Mode 59: Get AC Process History
 * C#: checkLetterSent() -> mode=59
 */
export const getACProcessHistory = async (appid) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/history/${appid}`);
        return response.data;
    } catch (error) {
        console.error('getACProcessHistory error:', error);
        throw error;
    }
};

/**
 * Mode 81: Check Want Certificate
 * C#: checkforProcessForOnsite() -> mode=81
 */
export const checkWantCertificate = async (appid) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/certificate/check-want/${appid}`);
        return response.data;
    } catch (error) {
        console.error('checkWantCertificate error:', error);
        throw error;
    }
};

/**
 * Mode 99: Check Date Time Validity
 * C#: checkforDate() -> mode=99
 */
export const checkDateTimeValidity = async (checkdate, day) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/validate/datetime`, {
            params: { checkdate, day }
        });
        return response.data;
    } catch (error) {
        console.error('checkDateTimeValidity error:', error);
        throw error;
    }
};

/**
 * Mode 106: Get Top AC Status with Adverse Stage
 * C#: checkResume() -> mode=106
 */
export const getTopACStatusWithAdverseStage = async (appid) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/ac-status/top-adverse/${appid}`);
        return response.data;
    } catch (error) {
        console.error('getTopACStatusWithAdverseStage error:', error);
        throw error;
    }
};

/**
 * Mode 107: Get Certificate Phase List
 */
export const getCertificatePhaseList = async (appid) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/certificate/phase-list/${appid}`);
        return response.data;
    } catch (error) {
        console.error('getCertificatePhaseList error:', error);
        throw error;
    }
};

/**
 * Mode 126: Check Want Certificate and Annexure
 * C#: checkDScertificate() -> mode=126
 */
export const checkWantCertificateAndAnnexure = async (appid) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/certificate/check-want-annexure/${appid}`);
        return response.data;
    } catch (error) {
        console.error('checkWantCertificateAndAnnexure error:', error);
        throw error;
    }
};

/**
 * Mode 128: Check Issue or Amendment Date
 * C#: checkILACforRMP() -> mode=128
 */
export const checkIssueOrAmendmentDate = async (appid) => {
    try {
        const response = await axios.get(`${AC_PROCESS_URL}/certificate/check-dates/${appid}`);
        return response.data;
    } catch (error) {
        console.error('checkIssueOrAmendmentDate error:', error);
        throw error;
    }
};

/**
 * Mode 152: Update Stage After Adverse Action
 * C#: UpdateStageAdversApllications() -> mode=152
 */
export const updateStageAfterAdverseAction = async (appid, orgid) => {
    try {
        const response = await axios.put(`${AC_PROCESS_URL}/adverse-action/update-stage`, {
            appid,
            orgid
        });
        return response.data;
    } catch (error) {
        console.error('updateStageAfterAdverseAction error:', error);
        throw error;
    }
};

// ==================== HELPER FUNCTIONS ====================

/**
 * Validate password
 */
export const validatePassword = async (password, orgId) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/auth/validate-password`, {
            password,
            orgId
        });
        return response.data.valid;
    } catch (error) {
        console.error('validatePassword error:', error);
        return false;
    }
};

/**
 * Update application stage (generic)
 */
export const updateApplicationStage = async (appid, stage) => {
    try {
        const response = await axios.put(`${AC_PROCESS_URL}/application-stage/update-stage`, {
            appid,
            stage,
        });
        return response.data;
    } catch (error) {
        console.error('updateApplicationStage error:', error);
        throw error;
    }
};

export const updateStageTo6e = async (acDate) => {
    try {
        const response = await axios.put(`${AC_PROCESS_URL}/updateStageTo6e`, {
            acDate,
        });
        return response.data;
    } catch (error) {
        console.error('updateStageTo6e error:', error);
        throw error;
    }
};

/**
 * Execute custom mode (generic executor for any mode)
 * C#: Mode not found fallback
 */
export const executeCustomMode = async (mode, params) => {
    try {
        const response = await axios.post(`${AC_PROCESS_URL}/execute-mode`, {
            mode,
            ...params
        });
        return response.data;
    } catch (error) {
        console.error('executeCustomMode error:', error);
        throw error;
    }
};

export const getMOMFilesFiltered = async (acDate, acType, acDateTo) => {
    try {
      const result = await axios.get(`${AC_PROCESS_URL}/mom-files/filtered`, {
        params: {
          acDate,
          acType,
          acDateTo
        }
      });
      return result.data;
    } catch (error) {
      console.error('getMOMFilesFiltered error:', error);
      throw error;
    }
  }


export default {
    // AC Date Management
    insertACDate,
    getACDatesByType,
    getACDatesDropdown,
    getAllACDates,
    getACDateById,
    updateACDate,
    checkExistingACDates,
    deactivateACDate,
    getACDateToByType,
    getRecentACDates,
    getACDateYears,
    getACDatesByYearMonth,
    getACDateToOptions,
    getACDateToByDate,
    
    // AC Process Management
    insertACProcess,
    updateACProcess,
    updateACProcessExtended,
    getACProcessByAppId,
    getApplicationDetails,
    getACProcessDetails,
    deactivateACProcess,
    getACStatusRemarks,
    rollbackACProcess,
    resetACProcess,
    
    // MOM File Management
    uploadMOMFile,
    autoFillMOMFile,
    getMOMFilesAll,
    getUploadedMOMFiles,
    getMOMFileByAppId,
    getMOMFormat,
    
    // AC Status Management
    updateACStatus,
    updateACStatusExtended,
    markStatus,
    updateCommitteeDecision,
    
    // Applications Management
    getApplications,
    searchApplications,
    getApplicationsByUsername,
    getApplicationsWithCondition,
    getApplicationByAppIdUsername,
    getApplicationsByOrgIdAndStage,
    getApplicationsByOrgIdPrevious,
    pullScopesFromPreviousApp,

    // Certificate Management
    getCertificateDetails,
    getCertificateForPrint,
    generateCertificateNumber,
    checkCertificateNumber,
    getCertificatesByOrgId,
    ceoApproveCertificate,
    updateCertificateDatesDetails,
    getCertificateScopeCount,
    
    // Grant Letter Management
    getGrantLetterDetails,
    markGrantLetterSent,
    checkGrantLetterSent,
    sendGrantLetterFinal,
    getGrantLetterHistory,
    getGrantLetterLatest,
    
    // Communication Letter
    getCommunicationLetterDetails,
    checkCommunicationLetterSent,
    getCommunicationHistory,
    checkCommunicationLetterStatus,
    
    // Decision Management
    getRemarks,
    insertAdvisoryUpdateStage,
    markDecision,
    getDecisionDetails,
    getStatusMarkedDetails,
    markDecisionCompleted,
    
    // DO Advisory
    insertDOAdvisory,
    getDOAdvisory,
    getDOAdvisoryDetails,
    checkAdvisoryVisibility,
    
    // Forward to JD
    forwardToJD,
    forwardToJDWithComment,
    getJDList,
    getAllJDList,
    checkJDAcceptance,
    
    // Discipline & Group
    getDisciplinesByLabType,
    getGroupsByDiscipline,
    getDistinctLabTypes,
    
    // Scope Management
    updateScopeGenerated,
    uploadScope,
    copyScopeToCertificate,
    copyPreviousScope,
    getCertificateScopeWithFlag,
    copyAndInsertScope,
    checkScopeGenerated,
    updateScopeURL,
    getScopeURL,
    getAllScopeURLs,
    
    // Miscellaneous
    getTopACStatus,
    getACProcessHistory,
    checkWantCertificate,
    checkDateTimeValidity,
    getTopACStatusWithAdverseStage,
    getCertificatePhaseList,
    checkWantCertificateAndAnnexure,
    checkIssueOrAmendmentDate,
    updateStageAfterAdverseAction,
    
    // Helpers
    validatePassword,
    updateApplicationStage,
    updateStageTo6e,
    executeCustomMode,
    getMOMFilesFiltered
};
